import { useCallback, useMemo } from 'react';

import type { ChatWindowProps, SuggestedAction } from '@verantos-dev/shared-components';

import type { UseChatWindowBaseResult } from './useChatWindowBase';
import type { UseGatewayChatLifecycleResult } from './useGatewayChatLifecycle';

interface SuggestedActionsBuilderArgs {
  setPrompt: (value: string) => void;
}

export type SuggestedActionsBuilder = (args: SuggestedActionsBuilderArgs) => SuggestedAction[];

export type FileUploadConfigOverride =
  | null
  | Partial<NonNullable<ChatWindowProps['fileUploadConfig']>>
  | ((
      defaults: NonNullable<ChatWindowProps['fileUploadConfig']>
    ) => ChatWindowProps['fileUploadConfig']);

export interface UseGatewayPromptPresentationOptions {
  prompt: UseChatWindowBaseResult['prompt'];
  isReady: boolean;
  activeConversationId: string;
  suggestedActionsBuilder?: SuggestedActionsBuilder;
  fileUploadConfigOverride?: FileUploadConfigOverride;
  queuePendingFirstMessage: UseGatewayChatLifecycleResult['queuePendingFirstMessage'];
  clearPendingFirstMessage: UseGatewayChatLifecycleResult['clearPendingFirstMessage'];
  createConversation: UseGatewayChatLifecycleResult['createConversation'];
}

export const useGatewayPromptPresentation = ({
  prompt,
  isReady,
  activeConversationId,
  suggestedActionsBuilder,
  fileUploadConfigOverride,
  queuePendingFirstMessage,
  clearPendingFirstMessage,
  createConversation,
}: UseGatewayPromptPresentationOptions) => {
  const suggestedActions = useMemo(() => {
    if (!suggestedActionsBuilder) {
      return [];
    }

    const setPrompt = (value: string) => prompt.onUserPromptChange(value);
    return suggestedActionsBuilder({ setPrompt });
  }, [prompt, suggestedActionsBuilder]);

  const handleClearAttachment = useCallback(() => {
    prompt.clearFileSelection?.();
    prompt.onUserPromptChange('');
  }, [prompt]);

  const defaultFileUploadConfig = useMemo(() => {
    if (!prompt.handleFileSelected) {
      return undefined;
    }

    const selectedFile = prompt.selectedFile;

    return {
      accept: '.csv',
      onFileSelected: prompt.handleFileSelected,
      isDisabled: !isReady,
      buttonAriaLabel: 'Upload CSV file',
      selectedFileName: prompt.selectedFileName,
      onClearSelectedFile: selectedFile ? handleClearAttachment : undefined,
    } satisfies NonNullable<ChatWindowProps['fileUploadConfig']>;
  }, [handleClearAttachment, isReady, prompt]);

  const fileUploadConfig = useMemo(() => {
    if (fileUploadConfigOverride === null) {
      return undefined;
    }

    if (!defaultFileUploadConfig) {
      return undefined;
    }

    if (typeof fileUploadConfigOverride === 'function') {
      return fileUploadConfigOverride(defaultFileUploadConfig);
    }

    if (fileUploadConfigOverride) {
      return {
        ...defaultFileUploadConfig,
        ...fileUploadConfigOverride,
      };
    }

    return defaultFileUploadConfig;
  }, [defaultFileUploadConfig, fileUploadConfigOverride]);

  const handleFileDropped = useCallback(
    (file: File) => {
      prompt.handleFileSelected?.(file);
    },
    [prompt]
  );

  const handleSendMessage = useCallback(async () => {
    try {
      if (!activeConversationId) {
        const currentMessage = prompt.userPrompt.trim();
        const currentFile = prompt.selectedFile;

        if (!currentMessage && !currentFile) {
          return;
        }

        queuePendingFirstMessage({
          message: currentMessage,
          file: currentFile ?? null,
        });

        prompt.onUserPromptChange('');

        await createConversation();
        return;
      }

      await prompt.sendPrompt();
    } catch (error) {
      console.error('Failed to send prompt', error);
      clearPendingFirstMessage();
    }
  }, [
    activeConversationId,
    clearPendingFirstMessage,
    createConversation,
    prompt,
    queuePendingFirstMessage,
  ]);

  return {
    suggestedActions,
    fileUploadConfig,
    handleFileDropped,
    handleSendMessage,
  };
};
